/**
 * Cover footer (Version / Generated By / Date) as a single-row table for Word DOCX
 * via html-docx-js — avoids position:absolute and white-space:nowrap, which Word often drops.
 */

/** Shared font stack for all UAE FDL report pages (cover, TOC, and content). */
export const UAE_FDL_REPORT_FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

function escapeCellText(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {Object} opts
 * @param {string} opts.versionLabel
 * @param {string} opts.generatedByLabel
 * @param {string} opts.dateLabel
 * @param {string|number} opts.version
 * @param {string} opts.generatedBy
 * @param {string} opts.currentDate
 * @param {'default'|'compact'} [opts.variant]
 */
const DOC_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';

const USER_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';

const CALENDAR_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';

function buildModernCoverMetadataColumn(label, value, iconSvg, iconBg) {
  const safeLabel = escapeCellText(label).toUpperCase();
  const safeValue = escapeCellText(value);

  return (
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;background-color:#ffffff;border:1px solid #e2e8f0;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="vertical-align:top;padding:12pt 14pt;">` +
    `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="width:36px;vertical-align:top;padding:0 10px 0 0;">` +
    `<div style="width:30px;height:30px;background-color:${iconBg};border-radius:8px;text-align:center;padding-top:8px;line-height:0;">${iconSvg}</div>` +
    `</td>` +
    `<td style="vertical-align:top;padding:0;line-height:1.35;mso-line-height-rule:exactly;">` +
    `<div style="font-size:8pt;color:#94a3b8;letter-spacing:1px;font-weight:700;margin:0 0 5px 0;">${safeLabel}</div>` +
    `<div style="font-size:12pt;color:#0f172a;font-weight:700;margin:0;">${safeValue}</div>` +
    `</td>` +
    `</tr>` +
    `</table>` +
    `</td>` +
    `</tr>` +
    `</table>`
  );
}

/**
 * Modern GHG-style cover footer with icon columns (Version / Generated By / Date).
 * @param {Object} opts
 * @param {string} opts.versionLabel
 * @param {string} opts.generatedByLabel
 * @param {string} opts.dateLabel
 * @param {string|number} opts.version
 * @param {string} opts.generatedBy
 * @param {string} opts.currentDate
 */
export function buildModernCoverMetadataTableHtml(opts) {
  const {
    versionLabel,
    generatedByLabel,
    dateLabel,
    version,
    generatedBy,
    currentDate,
  } = opts;

  const divider =
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin:0 0 20px 0;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="height:4px;background-color:#1d4ed8;font-size:0;line-height:0;width:68%;">&nbsp;</td>` +
    `<td style="height:4px;background-color:#d4a017;font-size:0;line-height:0;width:32%;">&nbsp;</td>` +
    `</tr>` +
    `</table>`;

  const columns =
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:separate;border-spacing:10px 0;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="width:24%;vertical-align:top;padding:0;">${buildModernCoverMetadataColumn(versionLabel, version, DOC_ICON_SVG, '#1d4ed8')}</td>` +
    `<td style="width:46%;vertical-align:top;padding:0;">${buildModernCoverMetadataColumn(generatedByLabel, generatedBy, USER_ICON_SVG, '#2563eb')}</td>` +
    `<td style="width:30%;vertical-align:top;padding:0;">${buildModernCoverMetadataColumn(dateLabel, currentDate, CALENDAR_ICON_SVG, '#d4a017')}</td>` +
    `</tr>` +
    `</table>`;

  return divider + columns;
}

/**
 * Modern report cover page (white background, no cover image).
 * @param {Object} opts
 * @param {string} opts.logoUrl
 * @param {string} opts.badgeLabel
 * @param {string} opts.reportingStandardsLabel
 * @param {string} opts.title
 * @param {string|number} opts.year
 * @param {string} opts.versionLabel
 * @param {string} opts.generatedByLabel
 * @param {string} opts.dateLabel
 * @param {string|number} opts.version
 * @param {string} opts.generatedBy
 * @param {string} opts.currentDate
 */
export function buildModernReportCoverHtml(opts) {
  const {
    logoUrl,
    badgeLabel,
    reportingStandardsLabel,
    title,
    year,
    versionLabel,
    generatedByLabel,
    dateLabel,
    version,
    generatedBy,
    currentDate,
  } = opts;

  const safeLogoUrl = escapeCellText(logoUrl);
  const safeBadge = escapeCellText(badgeLabel);
  const safeStandards = escapeCellText(reportingStandardsLabel).toUpperCase();
  const safeTitle = escapeCellText(title);
  const safeYear = escapeCellText(year);
  const metadata = buildModernCoverMetadataTableHtml({
    versionLabel,
    generatedByLabel,
    dateLabel,
    version,
    generatedBy,
    currentDate,
  });

  return (
    `<div style="page-break-after:always;background-color:#ffffff;border:1px solid #dbe3ee;border-radius:8px;width:8.27in;min-height:11.69in;margin:auto;box-sizing:border-box;font-family:${UAE_FDL_REPORT_FONT_FAMILY};">` +
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;min-height:11.69in;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="vertical-align:top;background-color:#ffffff;">` +
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;min-height:11.69in;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="vertical-align:top;padding:40pt 36pt 0 36pt;">` +
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="vertical-align:top;">&nbsp;</td>` +
    `<td style="width:170px;vertical-align:top;text-align:right;">` +
    `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-left:auto;background-color:#ffffff;border:1px solid #e2e8f0;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr><td style="padding:10pt 14pt;text-align:center;">` +
    `<img src="${safeLogoUrl}" alt="" style="max-width:130px;max-height:44px;height:auto;display:block;" />` +
    `</td></tr></table>` +
    `</td>` +
    `</tr>` +
    `</table>` +
    `<div style="margin-top:34pt;">` +
    `<div style="margin:0 0 10pt 0;">` +
    `<span style="display:inline-block;background-color:#1d4ed8;color:#ffffff;font-size:9pt;font-weight:700;padding:6pt 14pt;border-radius:20pt;letter-spacing:1.2px;line-height:1;text-transform:uppercase;">${safeBadge}</span>` +
    `</div>` +
    `<p style="margin:0;font-size:9pt;color:#64748b;letter-spacing:2px;font-weight:600;line-height:1.2;text-transform:uppercase;">${safeStandards}</p>` +
    `</div>` +
    `<h1 style="margin:22pt 0 0 0;font-size:38pt;font-weight:800;color:#1d4ed8;line-height:1.08;max-width:6in;padding:0;letter-spacing:-0.5px;">${safeTitle}</h1>` +
    `<p style="margin:16pt 0 0 0;font-size:58pt;font-weight:800;color:#0f172a;line-height:0.95;padding:0;letter-spacing:-1px;">${safeYear}</p>` +
    `<table cellpadding="0" cellspacing="0" border="0" style="margin-top:12pt;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr><td style="width:90px;height:5px;background-color:#1d4ed8;font-size:0;line-height:0;">&nbsp;</td>` +
    `<td style="width:30px;height:5px;background-color:#d4a017;font-size:0;line-height:0;">&nbsp;</td></tr>` +
    `</table>` +
    `</td>` +
    `</tr>` +
    `<tr>` +
    `<td style="vertical-align:bottom;padding:0 36pt 34pt 36pt;">` +
    metadata +
    `</td>` +
    `</tr>` +
    `</table>` +
    `</td>` +
    `</tr>` +
    `</table>` +
    `</div>`
  );
}

export function buildCoverMetadataTableHtml(opts) {
  const {
    versionLabel,
    generatedByLabel,
    dateLabel,
    version,
    generatedBy,
    currentDate,
    variant = 'default',
  } = opts;

  const v = escapeCellText(version);
  const g = escapeCellText(generatedBy);
  const d = escapeCellText(currentDate);
  const vl = escapeCellText(versionLabel);
  const gl = escapeCellText(generatedByLabel);
  const dl = escapeCellText(dateLabel);

  const fs = variant === 'compact' ? '10pt' : '11pt';

  return (
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="width:32%;font-size:${fs};color:#334155;vertical-align:top;padding:4pt 10pt 4pt 0;line-height:1.35;mso-line-height-rule:exactly;">` +
    `<span style="font-weight:bold;color:#475569;">${vl}</span> ${v}` +
    `</td>` +
    `<td style="width:36%;font-size:${fs};color:#334155;vertical-align:top;padding:4pt 10pt;border-left:solid 1pt #cbd5e1;line-height:1.35;mso-line-height-rule:exactly;">` +
    `<span style="font-weight:bold;color:#475569;">${gl}</span> ${g}` +
    `</td>` +
    `<td style="width:32%;font-size:${fs};color:#334155;vertical-align:top;padding:4pt 0 4pt 10pt;border-left:solid 1pt #cbd5e1;line-height:1.35;mso-line-height-rule:exactly;">` +
    `<span style="font-weight:bold;color:#475569;">${dl}</span> ${d}` +
    `</td>` +
    `</tr>` +
    `</table>`
  );
}
